import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, X, FileText, Trash2, Check, ArrowRight, Edit3 } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const TYPE_LABELS = {
  diseno: "Diseño",
  ugc: "UGC",
  filmmaker: "Filmmaker",
  ia_video: "Filmmaker IA",
};

const CHANNELS = [
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "facebook", label: "Facebook" },
  { id: "youtube_shorts", label: "YouTube Shorts" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "web", label: "Web Rubí" },
  { id: "app", label: "App Rubí" },
  { id: "sucursal", label: "Sucursal (impreso)" },
  { id: "pauta_meta", label: "Pauta Meta Ads" },
  { id: "pauta_tiktok", label: "Pauta TikTok Ads" },
];

const FORMATS = [
  { id: "post", label: "Post estático" },
  { id: "reel", label: "Reel (9:16)" },
  { id: "carrusel", label: "Carrusel" },
  { id: "story", label: "Story" },
  { id: "banner", label: "Banner web" },
  { id: "flyer", label: "Flyer impreso" },
  { id: "impreso", label: "Impreso (sucursal/escaparate)" },
  { id: "video_horizontal", label: "Video horizontal (16:9)" },
  { id: "video_vertical", label: "Video vertical" },
  { id: "video_largo", label: "Video largo" },
  { id: "live", label: "Live" },
];

const STATUS_COLORS = {
  borrador: "pill-gray",
  brief_incompleto: "pill-gray",
  en_espera: "pill-blue",
  en_produccion: "pill-yellow",
  en_revision: "pill-blue",
  requiere_cambios: "pill-red",
  aprobado: "pill-green",
  entregado: "pill-green",
  publicado: "pill-dark",
};

const Briefs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("new") === "1") setShowForm(true);
  }, [params]);

  const load = () => {
    setLoading(true);
    api.get("/briefs").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? items : items.filter((b) => b.type === filter);

  return (
    <Layout
      title="Briefs"
      subtitle="Aura genera 2 propuestas distintas y tú eliges"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-brief-btn">
          <Sparkles size={14} /> Nuevo brief
        </button>
      }
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {["all", "diseno", "ugc", "filmmaker", "ia_video"].map((t) => (
          <button key={t} onClick={() => setFilter(t)} className={`pill cursor-pointer ${filter === t ? "pill-yellow" : "pill-gray"}`} data-testid={`filter-${t}`}>
            {t === "all" ? "Todos" : TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <FileText size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin briefs</h3>
          <p className="text-[#666] mt-2 mb-5">Llena pocos datos y Aura te entrega 2 propuestas creativas distintas.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Crear primer brief</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => (
            <div key={b.id} onClick={() => setSelected(b)} className="aura-card p-5 cursor-pointer" data-testid={`brief-${b.id}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="pill pill-yellow">{TYPE_LABELS[b.type]}</span>
                <span className={`pill ${STATUS_COLORS[b.status] || "pill-gray"}`}>{b.status?.replace(/_/g, " ")}</span>
              </div>
              <h3 className="font-display text-base font-medium leading-tight">{b.title}</h3>
              <div className="text-xs text-[#666] mt-2 flex gap-2 flex-wrap">
                {b.channel && <span className="pill pill-gray text-[10px]">{b.channel}</span>}
                {b.deliverable_format && <span className="pill pill-gray text-[10px]">{b.deliverable_format}</span>}
              </div>
              {b.assignee_name && <div className="text-xs text-[#666] mt-2">Asignado a: {b.assignee_name}</div>}
              {b.due_date && <div className="text-xs text-[#666] mt-1">Entrega: {new Date(b.due_date).toLocaleDateString("es-MX")}</div>}
            </div>
          ))}
        </div>
      )}

      {showForm && <BriefProposalsFlow onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {selected && <BriefDetail brief={selected} onClose={() => setSelected(null)} onChange={() => { setSelected(null); load(); }} />}
    </Layout>
  );
};

const BriefProposalsFlow = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({
    type: "diseno",
    title: "",
    objective: "",
    product: "",
    branch: "",
    channel: "instagram",
    format: "post",
    assignee_id: "",
    assignee_name: "",
    campaign_id: "",
    due_date: "",
    priority: "media",
    message: "",
    references: "",
    extra: "",
  });
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const generateProposals = async () => {
    if (!form.title.trim() || !form.objective.trim()) { toast.error("Título y objetivo son requeridos"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/workflow/briefs/generate-proposals", { ...form, num_proposals: 2 });
      setProposals(res.data.proposals || []);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setGenerating(false);
    }
  };

  const pickProposal = (p) => {
    setChosen(p);
    setEditedTitle(p.title || form.title);
    setEditedContent(p.content);
    setStep(3);
  };

  const approveAndSave = async () => {
    setSaving(true);
    try {
      const res = await api.post("/workflow/briefs/approve-proposal", {
        title: editedTitle || form.title,
        content: editedContent,
        type: form.type,
        channel: form.channel,
        format: form.format,
        assignee_id: form.assignee_id || null,
        assignee_name: form.assignee_name,
        campaign_id: form.campaign_id || null,
        due_date: form.due_date || null,
        priority: form.priority,
      });
      toast.success(`Brief aprobado y enviado al calendario · ${res.data.brief?.assignee_name ? "Asignado a " + res.data.brief.assignee_name : "Listo"}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura · Brief Studio</div>
            <h2 className="font-display text-2xl font-medium">
              {step === 1 ? "Datos del brief" : step === 2 ? "Elige una propuesta" : "Edita y aprueba"}
            </h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#D99D26]" : "bg-[#F0F0F0]"}`} />)}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Tipo de brief</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <button key={k} onClick={() => setForm({ ...form, type: k })} className={`pill cursor-pointer ${form.type === k ? "pill-yellow" : "pill-gray"}`} data-testid={`brief-type-${k}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="aura-label">Título *</label>
                <input className="aura-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Banner promo 4x3 Instagram" data-testid="brief-title" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Objetivo *</label>
                <textarea className="aura-input" rows="2" required value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Comunicar promo 4x3 antes del fin de semana" />
              </div>
              <div>
                <label className="aura-label">Canal / Red social *</label>
                <select className="aura-input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="brief-channel">
                  {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Formato *</label>
                <select className="aura-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="brief-format">
                  {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Asignar a (creador) *</label>
                <select
                  className="aura-input"
                  value={form.assignee_id}
                  onChange={(e) => {
                    const u = users.find((x) => x.id === e.target.value);
                    setForm({ ...form, assignee_id: e.target.value, assignee_name: u?.name || "" });
                  }}
                  data-testid="brief-assignee"
                >
                  <option value="">— Sin asignar —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Fecha entrega</label>
                <input type="date" className="aura-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <label className="aura-label">Producto / Promoción</label>
                <input className="aura-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Perfumes árabes" />
              </div>
              <div>
                <label className="aura-label">Sucursal</label>
                <input className="aura-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="Tampico" />
              </div>
              <div>
                <label className="aura-label">Campaña</label>
                <select className="aura-input" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
                  <option value="">— Sin campaña —</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Prioridad</label>
                <select className="aura-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="aura-label">Mensaje principal</label>
                <input className="aura-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Promo solo este fin de semana" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Referencias / Notas extras</label>
                <textarea className="aura-input" rows="2" value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} placeholder="URLs, estilo, ideas..." />
              </div>
            </div>
            <button onClick={generateProposals} disabled={generating} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="generate-proposals-btn">
              {generating ? <><span className="spinner" /> Aura generando 2 propuestas distintas...</> : <><Sparkles size={14} /> Generar 2 propuestas</>}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-[#666]">Aura generó 2 propuestas creativas distintas. Elige la que mejor encaje.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {proposals.map((p, i) => (
                <div
                  key={p.id}
                  className="aura-card p-5 cursor-pointer hover:border-[#D99D26] hover:shadow-md transition-all"
                  onClick={() => pickProposal(p)}
                  data-testid={`proposal-${i}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className="pill pill-yellow text-[10px]">Propuesta {i + 1}</span>
                    <ArrowRight size={14} className="text-[#8a5e10]" />
                  </div>
                  <h3 className="font-display text-base font-medium mb-2">{p.title}</h3>
                  <div className="text-xs text-[#666] line-clamp-8 markdown-content">
                    <Markdown content={p.content.slice(0, 600)} />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="btn-ghost">← Volver</button>
          </div>
        )}

        {step === 3 && chosen && (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Título</label>
              <input className="aura-input" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} data-testid="brief-edited-title" />
            </div>
            <div>
              <label className="aura-label flex items-center gap-1"><Edit3 size={12} /> Brief completo (editable)</label>
              <textarea className="aura-input font-mono text-sm" rows="14" value={editedContent} onChange={(e) => setEditedContent(e.target.value)} data-testid="brief-edited-content" />
            </div>
            <div className="aura-card-flat p-4 bg-[#FFF9E6]/40 text-sm text-[#666]">
              <strong className="text-[#0A0A0A]">Al aprobar:</strong> el brief se guarda, se asigna a <strong>{form.assignee_name || "el creador"}</strong> y se agrega al calendario editorial para seguimiento.
            </div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="btn-ghost">← Cambiar propuesta</button>
              <button onClick={approveAndSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="approve-brief-btn">
                {saving ? <><span className="spinner" /> Guardando...</> : <><Check size={14} /> Aprobar y enviar al calendario</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BriefDetail = ({ brief, onClose, onChange }) => {
  const [status, setStatus] = useState(brief.status);
  const updateStatus = async (s) => {
    setStatus(s);
    await api.patch(`/briefs/${brief.id}`, { status: s });
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar brief?")) return;
    await api.delete(`/briefs/${brief.id}`);
    onChange();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">{TYPE_LABELS[brief.type]} · {brief.channel || "—"} · {brief.deliverable_format || "—"}</div>
            <h2 className="font-display text-2xl font-medium">{brief.title}</h2>
            {brief.assignee_name && <div className="text-sm text-[#666] mt-1">Asignado a {brief.assignee_name}</div>}
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {Object.keys(STATUS_COLORS).map((s) => (
            <button key={s} onClick={() => updateStatus(s)} className={`pill cursor-pointer ${status === s ? "pill-yellow" : "pill-gray"} text-[10px]`}>{s.replace(/_/g, " ")}</button>
          ))}
        </div>
        <div className="aura-card-flat p-5">
          <Markdown content={brief.ai_brief || "(Sin contenido generado)"} />
        </div>
        <div className="flex justify-between mt-5">
          <button onClick={remove} className="btn-ghost text-red-600"><Trash2 size={14} className="inline mr-1" /> Eliminar</button>
          <button onClick={() => { navigator.clipboard.writeText(brief.ai_brief || ""); toast.success("Copiado"); }} className="btn-outline">Copiar brief</button>
        </div>
      </div>
    </div>
  );
};

export default Briefs;
